import React, { useState, useEffect, useRef } from 'react';
import styled from "@emotion/styled";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import { Colors } from "./components/colors";
import NavBar from "./components/navbar.jsx";
import Footer from "./components/footer.jsx";
import Alert from "@mui/material/Alert";
import { useCompanies } from './contexts/CompanyContext';
import { denormalizeString } from './util.jsx';
import rolesData from '../roles.json';

const AddSalary = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { companyDisplayNames: companies, loading: companiesLoading } = useCompanies();
  const recaptchaRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    role: '',
    experience_in_yrs: '',
    education_level: '',
    location_country: 'Saudi Arabia',
    location_region: 'Ar Riyad',
    location_city: 'Riyadh',
    salary_per_month: '',
    currency: 'SAR'
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [alertStatus, setAlertStatus] = useState('');
  
  // Location data
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  // Searchable dropdown state
  const [companySearch, setCompanySearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  // Dropdown refs
  const companyDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);

  // Education levels - use the same array as original
  const educationLevels = t("addsalary_edu_options", { returnObjects: true });

  // Common currencies
  const currencies = [
    { code: 'SAR', name: 'Saudi Riyal' },
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'AED', name: 'UAE Dirham' },
    { code: 'KWD', name: 'Kuwaiti Dinar' },
    { code: 'QAR', name: 'Qatari Riyal' },
    { code: 'BHD', name: 'Bahraini Dinar' }
  ];

  // Get roles for dropdown
  const getRoleOptions = () => {
    const roleOptions = [];
    rolesData.roles.forEach(role => {
      const englishRoleName = denormalizeString(role.role_name_en);
      const arabicRoleName = role.role_name_ar;
      
      roleOptions.push({
        value: role.role_name_en,
        label: i18n.language === 'ar' ? arabicRoleName : englishRoleName
      });
    });
    return roleOptions;
  };

  // Filter companies based on search
  const getFilteredCompanies = () => {
    if (!companies) return [];
    if (!companySearch) return companies.slice(0, 10);
    return companies.filter(company => 
      company && company.toLowerCase().includes(companySearch.toLowerCase())
    ).slice(0, 10);
  };

  // Filter roles based on search
  const getFilteredRoles = () => {
    const allRoles = getRoleOptions();
    if (!roleSearch) return allRoles.slice(0, 10);
    return allRoles.filter(role => 
      role.label.toLowerCase().includes(roleSearch.toLowerCase())
    ).slice(0, 10);
  };

  // Handle company selection
  const handleCompanySelect = (company) => {
    setFormData(prev => ({ ...prev, company_name: company }));
    setCompanySearch(company);
    setShowCompanyDropdown(false);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    setFormData(prev => ({ ...prev, role: role.value }));
    setRoleSearch(role.label);
    setShowRoleDropdown(false);
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle location changes
  const handleLocationChange = async (type, value) => {
    try {
      if (type === 'country') {
        setFormData(prev => ({ 
          ...prev, 
          location_country: value, 
          location_region: '', 
          location_city: '' 
        }));
        
        // Load states for the selected country
        const country = countries.find(c => c.name === value);
        if (country) {
          const stateList = await GetState(country.id);
          setStates(stateList);
        } else {
          setStates([]);
        }
        setCities([]);
        
      } else if (type === 'region') {
        setFormData(prev => ({ 
          ...prev, 
          location_region: value, 
          location_city: '' 
        }));
        
        // Load cities for the selected region
        const country = countries.find(c => c.name === formData.location_country);
        if (country) {
          const state = states.find(s => s.name === value);
          
          if (state) {
            const cityList = await GetCity(country.id, state.id);
            setCities(cityList);
          } else {
            setCities([]);
          }
        }
      } else if (type === 'city') {
        setFormData(prev => ({ ...prev, location_city: value }));
      }
    } catch (error) {
      console.error('Error handling location change:', error);
      setCities([]);
    }
  };

  // Initialize location data
  useEffect(() => {
    const initializeLocationData = async () => {
      try {
        const countryList = await GetCountries();
        setCountries(countryList);
        
        // Initialize states for Saudi Arabia (default country)
        const saudiArabia = countryList.find(c => c.name === "Saudi Arabia");
        if (saudiArabia) {
          const stateList = await GetState(saudiArabia.id);
          setStates(stateList);
          
          // Initialize cities for Ar Riyad (default region) - try different name variations
          const arRiyadState = stateList.find(s => 
            s.name === "Ar Riyad" || 
            s.name === "Ar Riyadh" || 
            s.name.includes("Riyad") ||
            s.name === "Riyadh"
          );
          if (arRiyadState) {
            const cityList = await GetCity(saudiArabia.id, arRiyadState.id);
            setCities(cityList);
          }
        }
      } catch (error) {
        console.error('Error initializing location data:', error);
      }
    };
    
    initializeLocationData();
  }, []);
  
  // Load cities when default region is already selected
  useEffect(() => {
    const loadDefaultCities = async () => {
      if (formData.location_country === "Saudi Arabia" && 
          formData.location_region === "Ar Riyad" && 
          countries.length > 0 && 
          states.length > 0 && 
          cities.length === 0) {
        
        const country = countries.find(c => c.name === "Saudi Arabia");
        const state = states.find(s => s.name === "Ar Riyad" || s.name === "Ar Riyadh" || s.name.includes("Riyad"));
        
        if (country && state) {
          try {
            const cityList = await GetCity(country.id, state.id);
            setCities(cityList);
          } catch (error) {
            console.error('Error loading default cities:', error);
          }
        }
      }
    };
    
    loadDefaultCities();
  }, [countries, states, formData.location_country, formData.location_region, cities.length]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setShowCompanyDropdown(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaValue) {
      setAlertStatus("captcha-failed");
      return;
    }

    setLoading(true);
    setAlertStatus('');

    try {
      const response = await fetch('/api/v1/salary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          location_city: formData.location_city,
          location_country: formData.location_country.toLowerCase().replace(/\s+/g, '_'),
          location_state: formData.location_region.toLowerCase().replace(/\s+/g, '_'),
          salary_per_month: parseFloat(formData.salary_per_month),
          role: formData.role,
          experience_in_yrs: parseInt(formData.experience_in_yrs),
          captcha_value: captchaValue,
          education_level: formData.education_level,
          currency: formData.currency
        })
      });

      if (response.ok) {
        setAlertStatus("success");
        setTimeout(() => {
          navigate('/');
        }, 5000);
      } else {
        setAlertStatus("failed");
      }
    } catch (err) {
      setAlertStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  // Alert component
  const getAlert = () => {
    switch (alertStatus) {
      case "success":
        return <Alert severity="success">{t("addsalary_success")}</Alert>;
      case "failed":
        return <Alert severity="error">{t("addsalary_fail")}</Alert>;
      case "captcha-failed":
        return <Alert severity="error">{t("addsalary_captcha")}</Alert>;
      default:
        return null;
    }
  };

  return (
    <Container dir={i18n.dir()}>
      <NavBar />
      <FormContainer>
        {getAlert()}
        <Header>{t("addsalary_header")}</Header>

        <FormCard onSubmit={handleSubmit}>
          <FormGrid>
            
            {/* Company Name */}
            <FormField className="full-width">
              <Label>{t("addsalary_company")} *</Label>
              <SearchContainer ref={companyDropdownRef}>
                <SearchInput
                  type="text"
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    setFormData(prev => ({ ...prev, company_name: e.target.value }));
                    setShowCompanyDropdown(true);
                  }}
                  onFocus={() => setShowCompanyDropdown(true)}
                  placeholder={t("addsalary_company")}
                  required
                />
                {showCompanyDropdown && !companiesLoading && (
                  <DropdownList>
                    {getFilteredCompanies().map((company, index) => (
                      <DropdownItem
                        key={index}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleCompanySelect(company)}
                      >
                        {company}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </SearchContainer>
            </FormField>

            {/* Role */}
            <FormField className="full-width">
              <Label>{t("addsalary_role")} *</Label>
              <SearchContainer ref={roleDropdownRef}>
                <SearchInput
                  type="text"
                  value={roleSearch}
                  onChange={(e) => {
                    setRoleSearch(e.target.value);
                    setFormData(prev => ({ ...prev, role: e.target.value }));
                    setShowRoleDropdown(true);
                  }}
                  onFocus={() => setShowRoleDropdown(true)}
                  placeholder={t("addsalary_role")}
                  required
                />
                {showRoleDropdown && (
                  <DropdownList>
                    {getFilteredRoles().map((role, index) => (
                      <DropdownItem
                        key={index}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleRoleSelect(role)}
                      >
                        {role.label}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </SearchContainer>
            </FormField>

            {/* Years of Experience */}
            <FormField>
              <Label>{t("addsalary_exp")} *</Label>
              <Input
                type="number"
                min="0"
                max="50"
                value={formData.experience_in_yrs}
                onChange={(e) => handleInputChange('experience_in_yrs', e.target.value)}
                required
              />
            </FormField>

            {/* Education Level */}
            <FormField>
              <Label>{t("addsalary_edu")} ({t("optional")})</Label>
              <Select
                value={formData.education_level}
                onChange={(e) => handleInputChange('education_level', e.target.value)}
              >
                <option value="">{t("addsalary_edu")}</option>
                {educationLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Select>
            </FormField>

            {/* Country */}
            <FormField>
              <Label>{t("addsalary_country")} *</Label>
              <StyledCountryDropdown
                value={formData.location_country}
                onChange={(val) => handleLocationChange('country', val)}
                priorityOptions={['SA']}
              />
            </FormField>

            {/* Region/State */}
            <FormField>
              <Label>{t("addsalary_region")} *</Label>
              <StyledRegionDropdown
                country={formData.location_country}
                value={formData.location_region}
                onChange={(val) => handleLocationChange('region', val)}
              />
            </FormField>

            {/* City */}
            <FormField>
              <Label>{t("addsalary_city")} *</Label>
              <Select
                value={formData.location_city}
                onChange={(e) => handleLocationChange('city', e.target.value)}
                required
                disabled={cities.length === 0 && formData.location_region !== ''}
              >
                <option value="">
                  {cities.length === 0 && formData.location_region !== '' ? 'Loading cities...' : 
                   cities.length === 0 ? 'Select region first' : t("addsalary_city")}
                </option>
                {cities.map(city => (
                  <option key={city.id || city.name} value={city.name}>{city.name}</option>
                ))}
              </Select>
            </FormField>

            {/* Monthly Salary */}
            <FormField>
              <Label>{t("addsalary_salary")} *</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={formData.salary_per_month}
                onChange={(e) => handleInputChange('salary_per_month', e.target.value)}
                placeholder="5000"
                required
              />
            </FormField>
            
            {/* Currency */}
            <FormField>
              <Label>{t("addsalary_currency")} *</Label>
              <Select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </Select>
            </FormField>
          </FormGrid>

          {/* reCAPTCHA */}
          <CaptchaContainer>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LdzK2AqAAAAAKvla18dQdABViBMB6kBsoiwQqx5"
              onChange={setCaptchaValue}
              onExpired={() => setCaptchaValue('')}
            />
          </CaptchaContainer>

          {/* Submit Button */}
          <SubmitButton
            type="submit"
            disabled={loading || !captchaValue}
          >
            {loading ? t('Submitting...') : t("addsalary_submit")}
          </SubmitButton>
        </FormCard>
      </FormContainer>
      <Footer />
    </Container>
  );
};

export default AddSalary;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${Colors.lightBackground || '#f8f9fa'};
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const FormContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.h1`
  text-align: center;
  color: ${Colors.darkGreen || '#187108'};
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;
`;

const FormCard = styled.form`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0 0.5rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  column-gap: 2.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  
  .full-width {
    grid-column: 1 / -1;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    column-gap: 0;
    
    .full-width {
      grid-column: 1;
    }
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StyledCountryDropdown = styled(CountryDropdown)`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StyledRegionDropdown = styled(RegionDropdown)`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;


const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DropdownList = styled.div`
  position: absolute;
  z-index: 10;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 100%;
  margin-top: 0.25rem;
  max-height: 15rem;
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  
  &:hover {
    background-color: #f3f4f6;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const SalaryContainer = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const SalaryInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-right: none;
  border-radius: 6px 0 0 6px;
  font-size: 1rem;
  min-width: 0;
  width: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CurrencySelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0 6px 6px 0;
  font-size: 1rem;
  background-color: #f9fafb;
  width: 85px;
  flex-shrink: 0;
  flex-grow: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, transform 0.15s ease-in-out;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;